<template>
  <div
    :class="{'device-off': !deviceOn}"
    style="height: 100%;"
  >
    <div
      v-if="device && deviceOn"
      style="height: 100%;"
    >
      <Registrator v-if="device.device_type === deviceType.REGISTRATOR" />
      <Infoscreen v-else-if="device.device_type === deviceType.INFOSCREEN" />
      <OperatorWorkspace v-else-if="device.device_type === deviceType.OPERATOR_WORKSPACE" />
      <Windowscreen v-else-if="device.device_type === deviceType.WINDOWSCREEN" />
    </div>
  </div>
</template>

<script>
import Infoscreen from './devices/Infoscreen/components/Infoscreen';
import Registrator from './devices/Registrator/Registrator';
import OperatorWorkspace from './devices/OperatorWorkspace/OperatorWorkspace';
import Windowscreen from './devices/Windowscreen/Windowscreen';
import ActionsQueueService from '../services/ActionsQueueService';
import TicketAnimations from './devices/Infoscreen/helpers/TicketAnimations';
import InfoscreenConfig from './devices/Infoscreen/config';
import RegistratorConfig from './devices/Registrator/config';
import {createGlobalStore, parseJSON} from '../helpers/utils';
import { TAG_TYPE, USER_STATUSES_VISIBLE } from './devices/OperatorWorkspace/config';
import SocketMessages from '../helpers/socketMessages';
import AudioNotifier from '@/services/AudioNotifier/AudioNotifier';
import WebAudioApiPlayer from '@/services/AudioNotifier/WebAudioApiPlayer';
import DefaultAudioPlayer from '@/services/AudioNotifier/DefaultAudioPlayer';
import AudioFiles from './devices/Infoscreen/helpers/AudioFiles';
import DEVICE_TYPE from '@/constants/DeviceType';

export default {
  name: 'DevicePage',
  components: {
    Infoscreen,
    OperatorWorkspace,
    Registrator,
    Windowscreen
  },
  props: {
    token: {
      type: String,
      required: true
    },
    cacheMode: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      device: null,
      deviceType: DEVICE_TYPE,
      socketOpenUnsubscription: null,
      socketMessageUnsubscription: null,
      socketDeviceClearUnsubscription: null,
      SOCKET_EVENT: {
        [SocketMessages.ReloadDevice]: this.reloadDevice,
        [SocketMessages.EnableDevice]: this.enableDevice,
        [SocketMessages.DisableDevice]: this.disableDevice,
        [SocketMessages.DissociateDevice]: this.dissociateDevice
      },
      deviceOn: true,
      funcHasTimeout: null
    }
  },
  mounted() {
    if (this.$socketService.webSocketReady) {
      this.getDevice();
    } else {
      this.$socketService.connect(this.$deviceConfig.SOCKET_URL, this.$deviceConfig.SOCKET_ASSOCIATED_DEVICE_PARAM);
      this.socketOpenUnsubscription = this.$eventEmitter.subscribe(this.$eventEmitter.eventTypes.SOCKETS_OPEN, () => {
        this.getDevice();
      });
    }

    this.socketMessageUnsubscription = this.$eventEmitter.subscribe(this.$eventEmitter.eventTypes.SOCKETS_MESSAGE, (response) => {
      if (!response.payload) return;

      if (response.payload.event_type && response.payload.event_type === SocketMessages.ReloadDevice) {
        this.SOCKET_EVENT[SocketMessages.ReloadDevice]();
        return;
      } else if (response.payload.event_type && response.payload.event_type === SocketMessages.EnableDevice) {
        this.SOCKET_EVENT[SocketMessages.EnableDevice]();
        return;
      } else if (response.payload.event_type && response.payload.event_type === SocketMessages.DisableDevice) {
        this.SOCKET_EVENT[SocketMessages.DisableDevice]();
        return;
      } else if (response.payload.event_type && response.payload.event_type === SocketMessages.DissociateDevice) {
        this.SOCKET_EVENT[SocketMessages.DissociateDevice]();
        return;
      }

      parseJSON(response.payload.payload)
        .then((data) => {
          if (this.SOCKET_EVENT[response.payload.event_type]) {
            this.SOCKET_EVENT[response.payload.event_type](data);
          }
        });
    });
    this.socketDeviceClearUnsubscription = this.$eventEmitter.subscribe(this.$eventEmitter.eventTypes.CLEAR_DEVICE, () => {
      this.clearInvalidDevice();
    });
  },
  destroyed() {
    if (this.socketOpenUnsubscription) this.socketOpenUnsubscription();
    if (this.socketMessageUnsubscription) this.socketMessageUnsubscription();
    if (this.socketDeviceClearUnsubscription) this.socketDeviceClearUnsubscription();
  },
  methods: {
    getDevice() {
      localStorage.setItem(this.$deviceConfig.LOCALSTORAGE_CONSTANTS.DEVICE_TOKEN, this.token);

      const onError = (err) => {
        console.log('onError', err)
        console.trace('onError')
        if (err.response && err.response.data && err.response.data.code) {
          const errorCode = err.response.data.code;
          if (errorCode === this.$ERROR_CODE.NOT_ASSOCIATED_DEVICE || errorCode === this.$ERROR_CODE.DEVICE_NOT_FOUND) {
            this.clearInvalidDevice();
          } else if (errorCode === this.$ERROR_CODE.DEVICE_DISABLED) {
            this.deviceOn = false;
            this.$initService.authorizeDeviceByUID(this.token)
          }
        } else {
          this.clearInvalidDevice();
        }
      };

      this.runRecursiveCheckCacheFunction().then((func) => {
        this.$crudService.get('device/start_data/')
          .then((response) => {
            return this.$languageService.getAvailableLanguages(response.data)
              .then((resp) => {
                if (response.data.device.type === DEVICE_TYPE.INFOSCREEN) {
                  const playlists = [];

                  ['main_play_list', 'additional_play_list'].forEach((playlistParam) => {
                    if (
                      response.data[playlistParam] &&
                      response.data[playlistParam].items &&
                      response.data[playlistParam].items.length
                    ) {
                      playlists.push(func({
                        items: response.data[playlistParam].items,
                        playlistParam
                      }))
                    }
                  });

                  return Promise.all(playlists).then((cache) => {
                    let newResponseData = response.data;

                    cache.forEach((cacheItem) => {
                      if (
                        Object.prototype.hasOwnProperty.call(cacheItem, 'param') &&
                        Object.prototype.hasOwnProperty.call(cacheItem, 'items') &&
                        cacheItem.items.length
                      ) {
                        newResponseData = {
                          ...newResponseData,
                          [cacheItem.param]: {
                            ...newResponseData[cacheItem.param],
                            items: cacheItem.items
                          }
                        }
                      }
                    });

                    return [{...response, data: newResponseData}, resp];
                  });
                } else {
                  return [response, resp];
                }
              })
              .catch((error) => onError(error));
          })
          .then((resp) => {
            const deviceStartData = resp[0].data;
            const languageData = resp[1];
            if (deviceStartData.device) {
              this.initGlobalStore(deviceStartData, languageData);
              this.device = deviceStartData.device;

              if (this.$cacheService.canUseCache) {
                this.$cacheService.cleanUpCache();
              }

            } else {
              this.clearInvalidDevice();
            }
          })
          .catch((error) => onError(error));
      });
    },
    initGlobalStore(deviceStartData, languageData) {
      let globalDeviceData = {
        deviceStartData: deviceStartData,
        deviceConfig: this.deviceConfig,
        languages: languageData.availableLanguages,
        activeLanguage: languageData.activeLanguage,
        activeLanguageConsts: languageData.activeLanguageConsts,
        deviceUID: this.token,
      };

      switch (deviceStartData.device.type) {
      case DEVICE_TYPE.INFOSCREEN:
        globalDeviceData = {
          ...globalDeviceData,
          fontFamilyDefault: 'Inter',
          additional_playlist: this.handlePlaylist(deviceStartData.additional_play_list),
          infoscreenData: {
            actionsQueue: new ActionsQueueService(),
            fontNormalFamily: 'Inter',
            fontBoldFamily: 'Inter',
            fontSemiBoldFamily: 'Inter',
            infoboardBackgroundColor: deviceStartData.firm_style.infoboard_background || InfoscreenConfig.INFOBOARD_BACKGROUND_COLOR_DEFAULT,
            infoboardBackgroundColorMain: deviceStartData.firm_style.infoboard_background_main || InfoscreenConfig.INFOBOARD_BACKGROUND_COLOR_MAIN_DEFAULT,
            infoboardTicketNumberColor: deviceStartData.firm_style.infoboard_ticket_number || InfoscreenConfig.INFOBOARD_TICKET_NUMBER_COLOR_DEFAULT,
            infoboardWindowColor: deviceStartData.firm_style.infoboard_window || InfoscreenConfig.INFOBOARD_WINDOW_COLOR_DEFAULT,
            useNewTemplateDesign: deviceStartData.firm_style && deviceStartData.firm_style.infoboard_new_template_design !== undefined
              ? deviceStartData.firm_style.infoboard_new_template_design
              : InfoscreenConfig.INFOBOARD_NEW_TEMPLATE_DESIGN_DEFAULT,
            ticketCallAnimation: deviceStartData.is_fullscreen_call_used ? TicketAnimations.FULLSCREEN : TicketAnimations.COMMON,
            ticketIsCalling: false,
            externalSoundIsCalling: false,
            clientCallType: deviceStartData.unit_settings.client_call_types.find(clientCallType => clientCallType.id === deviceStartData.unit_settings.client_call).client_call_type,
            clientRecallType: deviceStartData.unit_settings.client_call_types.find(clientCallType => clientCallType.id === deviceStartData.unit_settings.client_recall).client_call_type,
            isMainPlaylistPlaylingSound: { enabled: false },
            isAdditionalPlaylistPlaylingSound: { enabled: false },
            audioNotifier: new AudioNotifier(AudioFiles, this.getAudioPlayer()),
          }
        }
        break;
      case DEVICE_TYPE.OPERATOR_WORKSPACE:
        const user = JSON.parse(localStorage.getItem(this.$deviceConfig.LOCALSTORAGE_CONSTANTS.USER));
        const ticketTags = globalDeviceData.deviceStartData.tags.filter(tag => tag.type === TAG_TYPE.TICKET);
        const operatorTags = globalDeviceData.deviceStartData.tags.filter(tag => tag.type === TAG_TYPE.OPERATOR);
        const isIssuingPoint = deviceStartData.window.issuing_point;
        const isEditRoute = deviceStartData.window.route_editing;
        const statuses = USER_STATUSES_VISIBLE.concat(
          operatorTags
            .filter(operatorTag => operatorTag.visible)
            .map(operatorTag => {
              return {
                id: operatorTag.id,
                title: operatorTag.name,
                icon: 'ri-price-tag-3-line'
              }
            })
        );

        globalDeviceData = {
          ...globalDeviceData,
          user,
          userRole: this.$authService.getRole(user),
          currentTicket: null,
          currentTicketServicing: null,
          currentStatus: null,
          currentStatusTimestamp: null,
          currentTicketTags: [],
          ticketTags,
          unsortedTickets: [],
          waitingTickets: [],
          postponedTickets: [],
          canceledTickets: [],
          foundTickets: [],
          readiedTickets: [],
          mobileTicketModalData: null,
          statuses,
          aliveModalTimer: null,
          autoCallTimer: '00:00',
          windows: [],
          ticketForContextMenu: null,
          ticketTransactionEnd: true,
          ticketRecallAccess: true,
          ticketRecallTimer: null,
          contextMenuActions: [],
          redirectMode: null,
          lastCurrentStateMode: null,
          statusTransactionEnd: true,
          isIssuingPoint,
          isEditRoute,
        };
        //
        break;
      case DEVICE_TYPE.REGISTRATOR:
        const useIssuingQueue = deviceStartData.office_settings.issuing_queue;

        globalDeviceData = {
          ...globalDeviceData,
          fontFamilyDefault: 'Inter',
          registratorBackgroundColor: deviceStartData.firm_style.registrator_background || RegistratorConfig.REGISTRATOR_BACKGROUND_COLOR_DEFAULT,
          registratorTextBtn: deviceStartData.firm_style.registrator_text_btn,
          registratorTextTop: deviceStartData.firm_style.registrator_text_top,
          registratorBackgroundBtnColor: deviceStartData.firm_style.registrator_background_btn,
          registratorBackgroundTopColor: deviceStartData.firm_style.registrator_background_top,
          registratorCaptionColor: deviceStartData.firm_style.registrator_caption,
          companyLogoImage: deviceStartData.firm_style.logo.file_url,
          mobileBackgroundImage: deviceStartData.firm_style.mobile_banner.file_url,
          currentLanguage: deviceStartData.languages.find(language => language.is_main),
          services: this.handleServices(deviceStartData.services),
          groupNames: this.handleGroupNames(deviceStartData.group_names),
          serviceVisitorsCount: deviceStartData.counter.reduce((acc, item) => {
            acc[item.office_service_id] = item.counter;
            return acc;
          }, {}),
          serviceTree: this.handleServiceTree(deviceStartData.services_tree),
          fontNormalFamily: 'Inter',
          fontBoldFamily: 'Inter',
          fontSemiBoldFamily: 'Inter',
          isDeviceMini: matchMedia('(max-width: 800px)').matches,
          isDeviceMobile: matchMedia('(max-width: 645px)').matches,
          isDeviceMax: matchMedia('(max-width: 1280px)').matches,
          ticketIsProcessing: false,
          authPhoneNumber: '',
          groupParamTitles: undefined,
          groupElements: undefined,
          useIssuingQueue,
          isNoPrintTicket: deviceStartData.is_print_ticket
        };

        window.onresize = () => {
          globalDeviceData.isDeviceMini = matchMedia('(max-width: 800px)').matches;
          globalDeviceData.isDeviceMobile = matchMedia('(max-width: 645px)').matches;
          globalDeviceData.isDeviceMax = matchMedia('(max-width: 1280px)').matches;
        }
        break;
      case DEVICE_TYPE.WINDOWSCREEN:
        globalDeviceData = {
          ...globalDeviceData,
          windowscreenData: {
            is_enabled: !!deviceStartData.device.is_enabled,
            firm_style: deviceStartData.firm_style || {},
            windows: deviceStartData.windows || [],
            operators_name_visible: !!deviceStartData.operators_name_visible,
            online_users: deviceStartData.online_users || {},
            window_current_session: deviceStartData.window_current_session || {},
            tickets: deviceStartData.Tickets || {}
          }
        };
        break;
      }
      createGlobalStore(globalDeviceData);
    },
    getAudioPlayer() {
      if (this.isSupportedWebAudioApi()) {
        return WebAudioApiPlayer;
      }

      return DefaultAudioPlayer;
    },
    isSupportedWebAudioApi() {
      return Boolean(window.AudioContext || window.webkitAudioContext);
    },
    handlePlaylist(playlist) {
      if (!playlist || !Array.isArray(playlist.items)) return;

      playlist.items.filter((playlistItem) => {
        if (playlistItem.widget && playlistItem.widget.name === 'text') {
          const widgetData = JSON.parse(playlistItem.meta);

          playlistItem.widgetMeta = {};

          for (const key in widgetData) {
            playlistItem.widgetMeta[key] = widgetData[key];
          }

          return widgetData.title || widgetData.text;

        }

        return true;
      });
    },
    handleServiceTree(items) {
      for (const item of items) {
        item.isDisabled = !item.is_office_visible || !item.is_unit_visible;

        if (item.group) {
          item.isGroupHasChildWithNumbers = this.isGroupHasChildWithNumbers(item);
        } else {
          item.isHasNumbers = this.hasNumbers(item);
        }

        if (item.group && item.child) this.handleServiceTree(item.child);
      }

      return items;
    },
    isGroupHasChildWithNumbers(item) {
      const stack = item.child ? [...item.child] : [];

      while (stack.length) {
        const groups = [];
        const currentItem = stack.pop();

        if (currentItem.group) {
          if (currentItem.isGroupHasChildWithNumbers) return true;
          if (currentItem.child) stack.push(...currentItem.child);
        } else {
          if (this.hasNumbers(currentItem)) return true;
        }
      }

      return false;
    },
    hasNumbers(item) {
      return Boolean(item.end_number && item.start_number);
    },
    handleServices(services) {
      return services.reduce((acc, service) => {
        service = this.fillTranslations(service);
        acc[service.id] = service;
        return acc;
      }, {});
    },
    handleGroupNames(groupNames) {
      return groupNames.reduce((acc, group) => {
        group = this.fillTranslations(group);
        acc[group.id] = group;
        return acc;
      }, {});
    },
    fillTranslations(entity) {
      let fallbackTranslationName = this.getFallbackTranslationName(entity);
      entity.fallbackTranslationName = fallbackTranslationName;
      return entity;
    },
    getFallbackTranslationName(service) {
      for (let languageTag in service.name) {
        const serviceName = service.name[languageTag];
        if (serviceName) return serviceName;
      }

      return '';
    },
    clearInvalidDevice() {
      this.$socketService.close();
      this.$socketService.webSocketReady = false;
      this.$emit('clearDevice');
    },
    reloadDevice() {
      window.location.reload();
    },
    disableDevice() {
      this.deviceOn = false;
    },
    enableDevice() {
      this.reloadDevice();
    },
    dissociateDevice() {
      if (this.device) {
        if (this.device.device_type !== this.deviceType.OPERATOR_WORKSPACE) {
          this.clearInvalidDevice();
        } else {
          this.$authService.logout().then(() => {
            this.clearInvalidDevice();
          });
        }
      } else {
        this.clearInvalidDevice();
      }
    },
    hasCacheFunction() {
      return new Promise((res, rej) => {
        if (!this.cacheMode) {
          res((data) => {
            return Promise.resolve(data);
          });
        } else {
          if (
            (Object.prototype.hasOwnProperty.call(window, 'getCacheForPlaylist') && typeof window.getCacheForPlaylist === 'function') ||
            (Object.prototype.hasOwnProperty.call(window.parent, 'getCacheForPlaylist') && typeof window.parent.getCacheForPlaylist === 'function') ||
            (
              Object.prototype.hasOwnProperty.call(window, 'PlaylistCache') &&
              Object.prototype.hasOwnProperty.call(window.PlaylistCache, 'getCacheForPlaylist') &&
              typeof window.PlaylistCache.getCacheForPlaylist === 'function'
            )
          ) {
            const androidFn = function (payload) {
              return new Promise((res, rej) => {
                if (typeof window.PlaylistCache.getCacheForPlaylist === 'function') {
                  window.PlaylistCache.getCacheForPlaylist(JSON.stringify(payload));
                  window[`reply_setCacheForPlaylist_${payload.playlistParam}`] = function (data) {
                    res(JSON.parse(data));
                  };
                }
              })
            };
            const fn = window.getCacheForPlaylist || window.parent.getCacheForPlaylist || androidFn;
            res(fn);
          } else if (this.$cacheService.canUseCache) {
            res(this.$cacheService.cache);
          } else {
            rej();
          }
        }
      })
    },
    runRecursiveCheckCacheFunction() {
      return new Promise((res, rej) => {
        this.hasCacheFunction().then((func) => {
          res(func);
        }).catch(() => {
          clearTimeout(this.funcHasTimeout);
          this.funcHasTimeout = setTimeout(() => {
            this.runRecursiveCheckCacheFunction().then((func) => {
              res(func);
            })
          }, 100);
        })
      })
    }
  }
}
</script>

<style lang="scss" scoped>
  .device-off {
    position: relative;
    &::after {
      content: '';
      width: 100vw;
      height: 100vh;
      position: absolute;
      top: 0;
      left: 0;
      background: #000;
      z-index: 999999999;
    }
  }
</style>
